import { useQuery } from "@tanstack/react-query"
import { apiFetch } from "../framework/api"
import { useAuthToken } from "./useAuthToken"

type UserRow = {
  email: string
  id: string
  name: string | null
  createdAt: string
  lastLoginAt: string
  hasActiveSubscription: boolean
  usedPromoCode: boolean
  idpName: string | null
  idProvider: string
  platform: string | null
  model: string | null
  version: string | null
  installId: string | null
}

type UsersResponse = {
  users: UserRow[]
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

type UseUsersParams = {
  filter?: string
  sort: string
  dir: "asc" | "desc"
  limit?: number
  offset?: number
  includeAnonymous: boolean
  includePaid: boolean
  includePromo: boolean
  includeTeam: boolean
}

export function useUsers({ filter, sort, dir, limit = 100, offset = 0, includeAnonymous, includePaid, includePromo, includeTeam }: UseUsersParams) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["users", { filter, sort, dir, limit, offset, includeAnonymous, includePaid, includePromo, includeTeam }],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set("limit", limit.toString())
      params.set("offset", offset.toString())
      params.set("includeAnonymous", includeAnonymous.toString())
      params.set("includePaid", includePaid.toString())
      params.set("includePromo", includePromo.toString())
      params.set("includeTeam", includeTeam.toString())
      if (filter) params.set("filter", filter)
      if (sort) params.set("sort", sort)
      if (dir) params.set("dir", dir)
      
      const data = await apiFetch<UsersResponse>(`/admin/users?${params.toString()}`, {
        token,
        method: "GET",
      })
      return data
    },
  })
}

type UserInstall = {
  id: number
  createdAt: string
  installId: string
  platform: string
  model: string
  version: string
}

type UserDetail = {
  databaseId: number
  uid: string
  email: string
  externalId: string
  idProvider: string
  idpName: string | null
  profileName: string
  createdAt: string
  updatedAt: string
  lastLoginAt: string
  hasActiveSubscription: boolean
  isReviewer: boolean
  subscriptionProductId: string | null
  subscriptionExpiresAt: string | null
  revenueCatCustomerId: string | null
  trialSessionsUsed: number
  promoCode: string | null
  promoCodeUsedAt: string | null
  installs: UserInstall[]
}

export type UserLog = {
  id: number
  createdAt: string
  installId: string | null
  platform: string | null
  appVersion: string | null
  entryCount: number
  firstLogAt: string | null
  lastLogAt: string | null
  blobSizeBytes: number
  compressedSizeBytes: number
  compressionRatioPercent: number
  source: string | null
  blobKey: string
  downloadUrl: string
  profileName: string
  processedDownloadUrl: string
}

type UserLogsResponse = {
  userUid: string
  logs: UserLog[]
}

export function useUserDetail(uid: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["user", uid],
    queryFn: async () => {
      const data = await apiFetch<UserDetail>(`/admin/users/${uid}`, {
        token,
        method: "GET",
      })
      return data
    },
    enabled: !!uid,
  })
}

export function useUserLogs(uid?: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["user-logs", uid],
    queryFn: async () => {
      const data = await apiFetch<UserLogsResponse>(`/admin/users/${uid}/logs`, {
        token,
        method: "GET",
      })
      return data.logs
    },
    enabled: !!uid,
  })
}
